import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import {
  Group,
  ListField,
  NodeField,
  ParagraphsField,
  PlainField,
  SaveBar,
  getPath,
} from "@/admin/fields";
import { SERVICE_ICON_NAMES } from "@/components/ServiceIcons";

const node = (text) => ({ text, size: null });

const sanitizeSlug = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const IconSelect = ({ label, value, onChange, testId }) => (
  <div className="af-field" data-testid={testId}>
    <div className="af-field-head">
      <span className="af-label">{label}</span>
      <span className="af-hint">Icon shown on cards and page headers</span>
    </div>
    <select
      className="af-input"
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
    >
      {!SERVICE_ICON_NAMES.includes(value) && <option value={value || ""}>{value || "None"}</option>}
      {SERVICE_ICON_NAMES.map((name) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))}
    </select>
  </div>
);

const RelatedPicker = ({ options, selected, currentSlug, onChange, testId }) => {
  const list = selected || [];
  const toggle = (slug) =>
    onChange(
      list.includes(slug)
        ? list.filter((item) => item !== slug)
        : [...list, slug]
    );

  return (
    <div className="af-field" data-testid={testId}>
      <div className="af-field-head">
        <span className="af-label">Related services</span>
        <span className="af-hint">
          Tick the services to cross-link from the bottom of this page
        </span>
      </div>
      <div className="af-check-grid">
        {options
          .filter((option) => option.slug !== currentSlug)
          .map((option) => (
            <label className="af-check" key={option.slug}>
              <input
                type="checkbox"
                checked={list.includes(option.slug)}
                onChange={() => toggle(option.slug)}
              />
              <span>
                {option.title}
                <em>{option.umbrella}</em>
              </span>
            </label>
          ))}
      </div>
      {list.filter((slug) => !options.some((option) => option.slug === slug))
        .length > 0 && (
        <span className="af-error">
          Some linked services no longer exist and will be skipped on the site.
        </span>
      )}
    </div>
  );
};

const ServicesTab = ({ content, update, saving, dirty, message, onSave }) => {
  const umbrellas = getPath(content, ["services", "umbrellas"]) || [];

  const options = umbrellas.flatMap((umbrella) =>
    (umbrella.services || []).map((service) => ({
      slug: service.slug,
      title: (service.title && service.title.text) || service.slug,
      umbrella: (umbrella.title && umbrella.title.text) || umbrella.slug,
    }))
  );

  const setUmbrellas = (next) => update(["services", "umbrellas"], next);

  const swap = (list, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) {
      return list;
    }
    const next = [...list];
    const held = next[index];
    next[index] = next[target];
    next[target] = held;
    return next;
  };

  const moveUmbrella = (index, direction) =>
    setUmbrellas(swap(umbrellas, index, direction));

  const addUmbrella = () => {
    const stamp = Date.now().toString(36);
    setUmbrellas([
      ...umbrellas,
      {
        slug: `new-group-${stamp}`,
        icon: "Boxes",
        badge: "",
        title: node("New Service Group"),
        hook: node("A short hook line for this group."),
        tagline: node("A one line summary used on the homepage card."),
        paragraphs: ["Describe what this group of services covers."],
        services: [],
      },
    ]);
  };

  const removeUmbrella = (index) => {
    const title = getPath(content, [
      "services",
      "umbrellas",
      index,
      "title",
      "text",
    ]);
    if (
      window.confirm(
        `Remove the group "${title}" and all services inside it? This cannot be undone after saving.`
      )
    ) {
      setUmbrellas(umbrellas.filter((item, itemIndex) => itemIndex !== index));
    }
  };

  const setServices = (umbrellaIndex, services) =>
    update(["services", "umbrellas", umbrellaIndex, "services"], services);

  const moveService = (umbrellaIndex, index, direction) => {
    const services = umbrellas[umbrellaIndex].services || [];
    setServices(umbrellaIndex, swap(services, index, direction));
  };

  const addService = (umbrellaIndex) => {
    const stamp = Date.now().toString(36);
    const services = umbrellas[umbrellaIndex].services || [];
    setServices(umbrellaIndex, [
      ...services,
      {
        slug: `new-service-${stamp}`,
        icon: "Boxes",
        title: node("New Service"),
        hook: node("A short hook line for this service."),
        teaser: node("A one or two line teaser used on the services page card."),
        paragraphs: ["Introduce the service here."],
        howWeHelp: ["Describe the first thing you do for the client."],
        idealFor: ["The type of business this suits."],
        related: [],
      },
    ]);
  };

  const removeService = (umbrellaIndex, index) => {
    const services = umbrellas[umbrellaIndex].services || [];
    const title = (services[index].title && services[index].title.text) || "";
    if (
      window.confirm(
        `Remove the service "${title}"? This cannot be undone after saving.`
      )
    ) {
      setServices(
        umbrellaIndex,
        services.filter((item, itemIndex) => itemIndex !== index)
      );
    }
  };

  return (
    <div data-testid="admin-tab-content-services">
      <SaveBar
        tabId="services"
        saving={saving}
        dirty={dirty}
        message={message}
        onSave={onSave}
      />

      <Group title="Services Page Header and Shared Labels" open>
        <div className="af-grid-2">
          <NodeField content={content} update={update} label="Title start" path={["services", "overview", "titleStart"]} />
          <NodeField content={content} update={update}
            label="Title accent (green)"
            path={["services", "overview", "titleAccent"]}
          />
        </div>
        <NodeField content={content} update={update} label="Intro" path={["services", "overview", "intro"]} multiline />
        <div className="af-grid-2">
          <NodeField content={content} update={update}
            label="Learn more label"
            path={["services", "overview", "learnMoreLabel"]}
          />
          <NodeField content={content} update={update}
            label='"How We Help" heading'
            path={["services", "overview", "howWeHelpLabel"]}
          />
        </div>
        <div className="af-grid-2">
          <NodeField content={content} update={update}
            label='"Ideal For" heading'
            path={["services", "overview", "idealForLabel"]}
          />
          <NodeField content={content} update={update}
            label='"Related Services" heading'
            path={["services", "overview", "relatedLabel"]}
          />
        </div>
        <div className="af-grid-2">
          <NodeField content={content} update={update}
            label="Banner heading"
            path={["services", "overview", "banner", "heading"]}
          />
          <NodeField content={content} update={update}
            label="Banner text"
            path={["services", "overview", "banner", "text"]}
            multiline
          />
        </div>
      </Group>

      <div className="af-blog-toolbar">
        <button
          type="button"
          className="af-btn af-btn-primary"
          onClick={addUmbrella}
          data-testid="admin-add-umbrella-btn"
        >
          <Plus size={13} />
          Add Service Group
        </button>
        <span className="af-hint">
          Groups appear on the Services page and as cards on the homepage, in this
          order.
        </span>
      </div>

      {umbrellas.map((umbrella, umbrellaIndex) => {
        const services = umbrella.services || [];
        const groupTitle =
          (umbrella.title && umbrella.title.text) || umbrella.slug;
        return (
          <Group
            title={`Group ${umbrellaIndex + 1}: ${groupTitle} (${services.length} services)`}
            key={umbrella.slug || umbrellaIndex}
          >
            <div className="af-row-actions">
              <button
                type="button"
                className="af-btn af-btn-ghost"
                onClick={() => moveUmbrella(umbrellaIndex, -1)}
                disabled={umbrellaIndex === 0}
                data-testid={`admin-umbrella-up-${umbrellaIndex}`}
              >
                <ArrowUp size={12} />
                Move up
              </button>
              <button
                type="button"
                className="af-btn af-btn-ghost"
                onClick={() => moveUmbrella(umbrellaIndex, 1)}
                disabled={umbrellaIndex === umbrellas.length - 1}
                data-testid={`admin-umbrella-down-${umbrellaIndex}`}
              >
                <ArrowDown size={12} />
                Move down
              </button>
              <button
                type="button"
                className="af-btn af-btn-danger"
                onClick={() => removeUmbrella(umbrellaIndex)}
                data-testid={`admin-remove-umbrella-${umbrellaIndex}`}
              >
                <Trash2 size={12} />
                Remove group
              </button>
            </div>

            <PlainField
              label="Anchor slug (used by the homepage card link)"
              value={umbrella.slug}
              onChange={(value) =>
                update(
                  ["services", "umbrellas", umbrellaIndex, "slug"],
                  sanitizeSlug(value)
                )
              }
              testId={`admin-umbrella-slug-${umbrellaIndex}`}
            />
            <IconSelect
              label="Group icon"
              value={umbrella.icon}
              onChange={(value) =>
                update(["services", "umbrellas", umbrellaIndex, "icon"], value)
              }
              testId={`admin-umbrella-icon-${umbrellaIndex}`}
            />
            <NodeField content={content} update={update}
              label="Group title"
              path={["services", "umbrellas", umbrellaIndex, "title"]}
            />
            <NodeField content={content} update={update}
              label="Hook line (green, under the title)"
              path={["services", "umbrellas", umbrellaIndex, "hook"]}
              multiline
            />
            <NodeField content={content} update={update}
              label="Tagline (used on the homepage card)"
              path={["services", "umbrellas", umbrellaIndex, "tagline"]}
              multiline
            />
            <ParagraphsField
              label="Group paragraphs (Services page)"
              items={
                getPath(content, [
                  "services",
                  "umbrellas",
                  umbrellaIndex,
                  "paragraphs",
                ]) || []
              }
              onChange={(items) =>
                update(
                  ["services", "umbrellas", umbrellaIndex, "paragraphs"],
                  items
                )
              }
            />

            <div className="af-blog-toolbar">
              <button
                type="button"
                className="af-btn af-btn-primary"
                onClick={() => addService(umbrellaIndex)}
                data-testid={`admin-add-service-${umbrellaIndex}`}
              >
                <Plus size={13} />
                Add Service to this group
              </button>
            </div>

            {services.map((service, serviceIndex) => {
              const base = [
                "services",
                "umbrellas",
                umbrellaIndex,
                "services",
                serviceIndex,
              ];
              const serviceTitle =
                (service.title && service.title.text) || service.slug;
              return (
                <Group
                  title={`${serviceIndex + 1}. ${serviceTitle}`}
                  key={service.slug || serviceIndex}
                >
                  <div className="af-row-actions">
                    <button
                      type="button"
                      className="af-btn af-btn-ghost"
                      onClick={() => moveService(umbrellaIndex, serviceIndex, -1)}
                      disabled={serviceIndex === 0}
                      data-testid={`admin-service-up-${umbrellaIndex}-${serviceIndex}`}
                    >
                      <ArrowUp size={12} />
                      Move up
                    </button>
                    <button
                      type="button"
                      className="af-btn af-btn-ghost"
                      onClick={() => moveService(umbrellaIndex, serviceIndex, 1)}
                      disabled={serviceIndex === services.length - 1}
                      data-testid={`admin-service-down-${umbrellaIndex}-${serviceIndex}`}
                    >
                      <ArrowDown size={12} />
                      Move down
                    </button>
                    <button
                      type="button"
                      className="af-btn af-btn-danger"
                      onClick={() => removeService(umbrellaIndex, serviceIndex)}
                      data-testid={`admin-remove-service-${umbrellaIndex}-${serviceIndex}`}
                    >
                      <Trash2 size={12} />
                      Remove service
                    </button>
                  </div>

                  <PlainField
                    label="URL slug (page address: /services/your-slug)"
                    value={service.slug}
                    onChange={(value) =>
                      update([...base, "slug"], sanitizeSlug(value))
                    }
                    testId={`admin-service-slug-${umbrellaIndex}-${serviceIndex}`}
                  />
                  <IconSelect
                    label="Service icon"
                    value={service.icon}
                    onChange={(value) => update([...base, "icon"], value)}
                    testId={`admin-service-icon-${umbrellaIndex}-${serviceIndex}`}
                  />
                  <NodeField content={content} update={update} label="Service title" path={[...base, "title"]} />
                  <NodeField content={content} update={update}
                    label="Hook line (large line under the page title)"
                    path={[...base, "hook"]}
                    multiline
                  />
                  <NodeField content={content} update={update}
                    label="Card teaser (Services page)"
                    path={[...base, "teaser"]}
                    multiline
                  />
                  <ParagraphsField
                    label="Intro paragraphs (detail page)"
                    items={getPath(content, [...base, "paragraphs"]) || []}
                    onChange={(items) => update([...base, "paragraphs"], items)}
                  />
                  <ListField
                    label="How We Help (one per line)"
                    items={getPath(content, [...base, "howWeHelp"]) || []}
                    onChange={(items) => update([...base, "howWeHelp"], items)}
                  />
                  <ListField
                    label="Ideal For (one per line)"
                    items={getPath(content, [...base, "idealFor"]) || []}
                    onChange={(items) => update([...base, "idealFor"], items)}
                  />
                  <RelatedPicker
                    options={options}
                    selected={getPath(content, [...base, "related"]) || []}
                    currentSlug={service.slug}
                    onChange={(items) => update([...base, "related"], items)}
                    testId={`admin-service-related-${umbrellaIndex}-${serviceIndex}`}
                  />
                </Group>
              );
            })}

            {services.length === 0 && (
              <p className="af-hint-block">
                This group has no services yet. It will show on the Services page
                with its description only.
              </p>
            )}
          </Group>
        );
      })}
    </div>
  );
};

export default ServicesTab;
